import React, { useState, useEffect } from 'react';
import { 
  Modal, 
  Table, 
  Button, 
  Checkbox, 
  Input, 
  message, 
  Card, 
  Tag, 
  Space, 
  Divider,
  Select,
  Form,
  Row,
  Col,
  Statistic,
  Tooltip
} from 'antd';
import { 
  CheckOutlined, 
  CloseOutlined, 
  EyeOutlined,
  DownloadOutlined,
  AppstoreOutlined
} from '@ant-design/icons';
import { useAuth } from '../context/AuthContext.jsx';
import apiService from '../services/apiService';

const { TextArea } = Input;
const { Option } = Select;

const AppDetailsModal = ({ 
  visible, 
  onClose, 
  sessionId, 
  projectId, 
  statusType, 
  statusTitle, 
  initialData = null 
}) => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedApps, setSelectedApps] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionForm] = Form.useForm();
  const { getAuthToken } = useAuth();

  // Debug environment variable
  console.log('REACT_APP_ENABLE_APPROVAL_ACTIONS:', process.env.REACT_APP_ENABLE_APPROVAL_ACTIONS);
  
  // Create a more robust check for the approval actions
  const approvalActionsEnabled = process.env.REACT_APP_ENABLE_APPROVAL_ACTIONS?.toLowerCase().trim() === 'true';

  // Load apps when modal opens
  useEffect(() => {
    if (visible && sessionId && projectId && statusType) {
      loadAppsData();
    }
  }, [visible, sessionId, projectId, statusType]);

  const loadAppsData = async () => {
    try {
      setLoading(true);
      const token = getAuthToken();
      
      let appsData = [];
      if (initialData && initialData.apps) {
        // Use provided data if available
        appsData = initialData.apps;
      } else {
        // Fetch from API
        const response = await apiService.getAnalysisResults(
          sessionId, 
          projectId, 
          token, 
          statusType
        );
        appsData = response.results_by_type[statusType]?.apps || [];
      }
      
      setApps(appsData);
    } catch (error) {
      console.error('Failed to load apps data:', error);
      message.error('Failed to load app details');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectChange = (selectedRowKeys, selectedRows) => {
    setSelectedRowKeys(selectedRowKeys);
    setSelectedApps(selectedRows);
  };

  const handleBulkAction = async (action) => {
    if (selectedApps.length === 0) {
      message.warning('Please select apps to process');
      return;
    }

    try {
      const values = await actionForm.validateFields();
      setActionLoading(true);
      
      const token = getAuthToken();
      const appIds = selectedApps.map(app => app.app_id);
      
      const response = await apiService.approveDeclineApps(
        sessionId,
        projectId,
        action,
        appIds,
        values.reason,
        token
      );

      message.success(`Successfully ${action}d ${response.processed_apps} apps`);
      
      // Clear selection and form
      setSelectedRowKeys([]);
      setSelectedApps([]);
      actionForm.resetFields();
      
      // Refresh data
      loadAppsData();
      
    } catch (error) {
      console.error(`Failed to ${action} apps:`, error);
      message.error(`Failed to ${action} apps: ${error.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 70) return '#ef4444';
    if (score >= 40) return '#f97316';
    return '#22c55e';
  };

  const getOutcomeColor = (outcome) => {
    switch(outcome?.toLowerCase()) {
      case 'approve':
      case 'pass':
        return 'green';
      case 'decline':
      case 'fail':
        return 'red';
      case 'refer':
        return 'orange';
      default:
        return 'blue';
    }
  };

  const renderScorecardScore = (score) => {
    if (score === 'no match') {
      return (
        <Tag 
          size="small" 
          style={{ 
            fontSize: '10px', 
            backgroundColor: '#374151',
            color: '#9ca3af',
            border: '1px solid #4b5563',
            margin: 0
          }}
        >
          No Match
        </Tag>
      );
    }
    if (score === "match") {
      return (
        <Tag
          size="small"
          style={{
            fontSize: "10px",
            backgroundColor: "#374151",
            color: "#9ca3af",
            border: "1px solid #4b5563",
            margin: 0,
          }}
        >
          Match
        </Tag>
      );
    }
    if (score !== undefined && score !== null && typeof score === 'number') {
      // Convert 0-1 scale to 0-100 for color calculation (getScoreColor expects 0-100 range)
      const colorScore = score * 100;
      return (
        <Tag 
          size="small" 
          color={getScoreColor(colorScore)} 
          style={{ 
            fontSize: '11px', 
            fontWeight: 'bold',
            margin: 0,
            minWidth: '32px',
            textAlign: 'center'
          }}
        >
          {score.toFixed(1)}
        </Tag>
      );
    }
    return (
      <span style={{ 
        color: '#6b7280', 
        fontSize: '11px',
        fontStyle: 'italic'
      }}>
        N/A
      </span>
    );
  };

  const columns = [
    {
      title: 'App ID',
      dataIndex: 'app_id',
      key: 'app_id',
      width: 120,
      render: (appId) => (
        <span style={{ 
          fontFamily: 'monospace', 
          fontSize: '12px',
          color: '#94a3b8',
          fontWeight: '500'
        }}>
          {appId}
        </span>
      )
    },
    {
      title: 'App Name',
      dataIndex: 'app_name',
      key: 'app_name',
      width: 200,
      render: (name) => (
        <Tooltip title={name || 'Unknown App'}>
          <span style={{ 
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '180px',
            color: '#e2e8f0',
            fontSize: '13px'
          }}>
            {name || <em style={{ color: '#9ca3af' }}>Unknown App</em>}
          </span>
        </Tooltip>
      )
    },
    {
      title: (
        <Tooltip title="Scorecard 1 Score">
          <span>SC1</span>
        </Tooltip>
      ),
      key: 'scorecard_1',
      width: 70,
      align: 'center',
      render: (_, record) => {
        return renderScorecardScore(record.scorecard_scores?.scorecard_1);
      }
    },
    {
      title: (
        <Tooltip title="Scorecard 2 Score">
          <span>SC2</span>
        </Tooltip>
      ),
      key: 'scorecard_2',
      width: 70,
      align: 'center',
      render: (_, record) => {
        return renderScorecardScore(record.scorecard_scores?.scorecard_2);
      }
    },
    {
      title: (
        <Tooltip title="Scorecard 3 Score">
          <span>SC3</span>
        </Tooltip>
      ),
      key: 'scorecard_3',
      width: 70,
      align: 'center',
      render: (_, record) => {
        return renderScorecardScore(record.scorecard_scores?.scorecard_3);
      }
    },
    {
      title: (
        <Tooltip title="Scorecard 4 Score">
          <span>SC4</span>
        </Tooltip>
      ),
      key: 'scorecard_4',
      width: 70,
      align: 'center',
      render: (_, record) => {
        return renderScorecardScore(record.scorecard_scores?.scorecard_4);
      }
    },
    {
      title: 'Evaluation Reasons',
      key: 'reasons',
      width: 180,
      render: (_, record) => {
        const reasons = record.evaluation_details?.reasons || [];
        if (reasons.length === 0) return <span style={{ color: '#666' }}>No reasons</span>;
        
        return (
          <div style={{ maxWidth: '160px' }}>
            {reasons.slice(0, 1).map((reason, index) => (
              <Tag key={index} size="small" style={{ marginBottom: 2 }}>
                {reason}
              </Tag>
            ))}
            {reasons.length > 1 && (
              <Tooltip title={reasons.slice(1).join(', ')}>
                <Tag size="small">+{reasons.length - 1} more</Tag>
              </Tooltip>
            )}
          </div>
        );
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      align: 'center',
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button 
              type="text" 
              size="small" 
              icon={<EyeOutlined />}
              onClick={() => {
                // Could open a detailed view modal here
                message.info('Detailed view coming soon');
              }}
            />
          </Tooltip>
        </Space>
      )
    }
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: handleSelectChange,
    getCheckboxProps: (record) => ({
      name: record.app_id,
    }),
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <AppstoreOutlined />
          <span>{statusTitle} Apps Details</span>
          <Tag color="blue">{apps.length} apps</Tag>
        </div>
      }
      open={visible}
      onCancel={onClose}
      width={1400}
      footer={null}
      destroyOnClose
    >
      <style>
        {`
          .ant-table-thead > tr > th {
            background-color: #1f2937 !important;
            color: #e5e7eb !important;
            border-bottom: 1px solid #374151 !important;
            font-weight: 600;
            text-align: center;
          }
          .ant-table-tbody > tr > td {
            border-bottom: 1px solid #2d3748 !important;
            background-color: #1a202c !important;
            color: #e2e8f0 !important;
            padding: 8px 12px !important;
          }
          .ant-table-tbody > tr:hover > td {
            background-color: rgba(55, 65, 81, 0.8) !important;
            color: #ffffff !important;
          }
          .ant-table-tbody > tr:hover > td .ant-tag {
            filter: brightness(1.2);
          }
          .ant-table-tbody > tr:hover > td span {
            color: #ffffff !important;
          }
          .table-row-even > td {
            background-color: #1a202c !important;
          }
          .table-row-odd > td {
            background-color: #2d3748 !important;
          }
          .ant-table-selection-column {
            text-align: center !important;
          }
          .ant-table-tbody > tr:hover > td em {
            color: #d1d5db !important;
          }
          .ant-table-tbody > tr:hover > td .ant-tooltip-open {
            color: #ffffff !important;
          }
          .ant-pagination {
            display: flex;
            justify-content: center;
            align-items: center;
            margin-top: 16px;
          }
          .ant-pagination .ant-pagination-item {
            background-color: #374151 !important;
            border: 1px solid #4b5563 !important;
            border-radius: 6px;
            margin: 0 4px;
          }
          .ant-pagination .ant-pagination-item a {
            color: #e5e7eb !important;
            font-weight: 500;
          }
          .ant-pagination .ant-pagination-item:hover {
            background-color: #4b5563 !important;
            border-color: #6b7280 !important;
          }
          .ant-pagination .ant-pagination-item:hover a {
            color: #ffffff !important;
          }
          .ant-pagination .ant-pagination-item-active {
            background-color: #3b82f6 !important;
            border-color: #3b82f6 !important;
          }
          .ant-pagination .ant-pagination-item-active a {
            color: #ffffff !important;
            font-weight: 600;
          }
          .ant-pagination .ant-pagination-prev,
          .ant-pagination .ant-pagination-next {
            background-color: #374151 !important;
            border: 1px solid #4b5563 !important;
            border-radius: 6px;
          }
          .ant-pagination .ant-pagination-prev:hover,
          .ant-pagination .ant-pagination-next:hover {
            background-color: #4b5563 !important;
            border-color: #6b7280 !important;
          }
          .ant-pagination .ant-pagination-prev .anticon,
          .ant-pagination .ant-pagination-next .anticon,
          .ant-pagination .ant-pagination-prev:hover .anticon,
          .ant-pagination .ant-pagination-next:hover .anticon {
            color: #e5e7eb !important;
          }
          .ant-pagination .ant-pagination-options {
            color: #e5e7eb !important;
          }
          .ant-pagination .ant-select-selector {
            background-color: #374151 !important;
            border: 1px solid #4b5563 !important;
            color: #e5e7eb !important;
          }
          .ant-pagination .ant-select-arrow {
            color: #e5e7eb !important;
          }
          .ant-pagination .ant-pagination-total-text {
            color: #9ca3af !important;
            font-size: 13px;
          }
        `}
      </style>
      <div style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={8}>
            <Card size="small">
              <Statistic 
                title="Total Apps" 
                value={apps.length}
                prefix={<AppstoreOutlined />}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card size="small">
              <Statistic 
                title="Selected" 
                value={selectedApps.length}
                prefix={<CheckOutlined />}
              />
            </Card>
          </Col>
          {approvalActionsEnabled && (
            <Col span={8}>
              <Card size="small">
                <Statistic 
                  title="Actions Available" 
                  value={selectedApps.length > 0 ? 2 : 0}
                  suffix="/ 2"
                />
              </Card>
            </Col>
          )}
        </Row>
      </div>

      {selectedApps.length > 0 && approvalActionsEnabled && (
        <Card 
          size="small" 
          style={{ 
            marginBottom: 16, 
            backgroundColor: '#f0f9ff',
            border: '1px solid #0ea5e9'
          }}
        >
          <Form form={actionForm} layout="vertical">
            <Row gutter={16} align="middle">
              <Col span={12}>
                <Form.Item 
                  name="reason" 
                  label="Action Reason"
                  rules={[{ required: true, message: 'Please provide a reason' }]}
                >
                  <TextArea 
                    rows={2}
                    placeholder="Enter reason for approval/decline..."
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <div style={{ display: 'flex', gap: 8, paddingTop: 24 }}>
                  <Button
                    type="primary"
                    icon={<CheckOutlined />}
                    loading={actionLoading}
                    disabled={selectedApps.length === 0}
                    onClick={() => handleBulkAction('approve')}
                    style={{ flex: 1 }}
                  >
                    Approve {selectedApps.length} Apps
                  </Button>
                  <Button
                    danger
                    icon={<CloseOutlined />}
                    loading={actionLoading}
                    disabled={selectedApps.length === 0}
                    onClick={() => handleBulkAction('decline')}
                    style={{ flex: 1 }}
                  >
                    Decline {selectedApps.length} Apps
                  </Button>
                </div>
              </Col>
            </Row>
          </Form>
        </Card>
      )}

      <Divider />

      {apps.length > 0 && (
        <Card 
          size="small" 
          style={{ 
            marginBottom: 16, 
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}
        >
          <Row gutter={16}>
            <Col span={6}>
              <Statistic 
                title={<span style={{ color: '#e2e8f0', fontSize: '12px' }}>SC1 Average</span>}
                value={
                  apps
                    .filter(app => typeof app.scorecard_scores?.scorecard_1 === 'number')
                    .reduce((acc, app) => acc + app.scorecard_scores.scorecard_1, 0) /
                  Math.max(1, apps.filter(app => typeof app.scorecard_scores?.scorecard_1 === 'number').length)
                } 
                precision={2}
                valueStyle={{ fontSize: '16px', color: '#ffffff', fontWeight: 'bold' }}
              />
            </Col>
            <Col span={6}>
              <Statistic 
                title={<span style={{ color: '#e2e8f0', fontSize: '12px' }}>SC2 Average</span>}
                value={
                  apps
                    .filter(app => typeof app.scorecard_scores?.scorecard_2 === 'number')
                    .reduce((acc, app) => acc + app.scorecard_scores.scorecard_2, 0) /
                  Math.max(1, apps.filter(app => typeof app.scorecard_scores?.scorecard_2 === 'number').length)
                } 
                precision={2}
                valueStyle={{ fontSize: '16px', color: '#ffffff', fontWeight: 'bold' }}
              />
            </Col>
            <Col span={6}>
              <Statistic 
                title={<span style={{ color: '#e2e8f0', fontSize: '12px' }}>SC3 Matches</span>}
                value={apps.filter(app => typeof app.scorecard_scores?.scorecard_3 === 'number').length}
                suffix={<span style={{ color: '#94a3b8' }}>/ {apps.length}</span>}
                valueStyle={{ fontSize: '16px', color: '#ffffff', fontWeight: 'bold' }}
              />
            </Col>
            <Col span={6}>
              <Statistic 
                title={<span style={{ color: '#e2e8f0', fontSize: '12px' }}>SC4 Matches</span>}
                value={apps.filter(app => typeof app.scorecard_scores?.scorecard_4 === 'number').length}
                suffix={<span style={{ color: '#94a3b8' }}>/ {apps.length}</span>}
                valueStyle={{ fontSize: '16px', color: '#ffffff', fontWeight: 'bold' }}
              />
            </Col>
          </Row>
        </Card>
      )}

      <Table
        rowSelection={approvalActionsEnabled ? rowSelection : undefined}
        columns={columns}
        dataSource={apps}
        rowKey="app_id"
        loading={loading}
        size="small"
        scroll={{ y: 400 }}
        pagination={{
          pageSize: 50,
          showSizeChanger: true,
          showTotal: (total, range) => 
            `${range[0]}-${range[1]} of ${total} apps`,
        }}
        rowClassName={(record, index) => 
          index % 2 === 0 ? 'table-row-even' : 'table-row-odd'
        }
        style={{
          '--table-header-bg': '#1f2937',
          '--table-row-hover-bg': 'rgba(59, 130, 246, 0.1)',
        }}
      />
    </Modal>
  );
};

export default AppDetailsModal;